/**
 * SegStudyHome - Urinary Stone 3D Annotations study home page
 *
 * Shows phases with a pill-tab selector, each phase containing
 * batch cards with per-batch progress and annotator assignment.
 *
 * Project ID format: us3d-phase{NN}-batch{NN}
 * URL: /labeling/urinary-stone
 */

import { useState, useMemo, useRef, useEffect, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import axios from "axios";
import { getApiBaseUrl } from "../../config/api";
import { userIdAtom } from "../../atoms/authAtoms";
import {
  useProjectsQuery,
  useProjectStatsQuery,
  useWorklistQuery,
  type ProjectStatsResponse,
  type AnnotationStatsResponse,
} from "../../hooks/useLabelingQueries";
import { bucketStats } from "../../utils/caseStatus";
import { dayIndex } from "../../utils/dayBucket";
import { FaChevronRight, FaUser, FaFileAlt, FaCircle, FaCheckCircle } from "../../utils/icons";
import { InstructionsDialog } from "./InstructionsDialog";
import { ProjectCaseList } from "./ProjectCaseList";
import { US3D_REGEX } from "../../utils/studyGroups";
import { canViewStats } from "../../utils/statsGate";

interface BatchInfo {
  batchNum: number;
  batchLabel: string;
  projectId: string;
  totalCases: number;
  studyUids: string[];
  assignedTo?: { name: string; email: string };
}

interface PhaseInfo {
  phaseNum: number;
  phaseLabel: string;
  description: string;
  batches: BatchInfo[];
}

/** Descriptions for each phase — keyed by phase number */
const PHASE_DESCRIPTIONS: Record<number, string> = {
  1: "Preliminary assessment and measurement of inter-radiologist agreement",
  2: "First full annotation set — a chance to get familiar with the system and surface suggestions",
};

// ── Phase-level progress aggregation ───────────────────────────────────────────

/**
 * Aggregates per-batch progress into per-phase completion percentages.
 *
 * Reads from /stats (single aggregate response per project) — the
 * backend computes completed_cases / in_progress_cases / total_cases
 * there, so we don't need to pull the full case list per batch just
 * to render bars. The popover (ProjectCaseList) still uses /cases
 * because it needs the per-case rows.
 */

function usePhaseProgress(phases: PhaseInfo[]) {
  const allBatches = useMemo(() => phases.flatMap((p) => p.batches), [phases]);

  // Use /stats (single aggregate per project) instead of /cases (full list)
  // — at typical batch counts this is the difference between O(N) small
  // payloads and O(N) large payloads on every dashboard render.
  const statsQueries = useQueries({
    queries: allBatches.map((b) => ({
      queryKey: ["project-stats", b.projectId] as const,
      queryFn: async () => {
        const response = await axios.get<ProjectStatsResponse>(
          `${getApiBaseUrl()}/api/labeling/projects/${b.projectId}/stats`,
        );
        return response.data;
      },
      refetchInterval: 10000,
      retry: 2,
    })),
  });

  // Stable serialization so useMemo only recomputes when values change.
  const progressKey = statsQueries
    .map((q) => {
      const b = bucketStats(q.data);
      return `${b.completed}/${b.inProgress}/${b.total}`;
    })
    .join(",");

  const phaseCompletion = useMemo(() => {
    const buckets = new Map<string, ReturnType<typeof bucketStats>>();
    allBatches.forEach((b, i) => {
      buckets.set(b.projectId, bucketStats(statsQueries[i]?.data));
    });

    const result = new Map<number, { completedPct: number; inProgressPct: number }>();
    for (const phase of phases) {
      let total = 0;
      let completed = 0;
      let inProgress = 0;
      for (const b of phase.batches) {
        const x = buckets.get(b.projectId);
        if (!x) continue;
        total += x.total;
        completed += x.completed;
        inProgress += x.inProgress;
      }
      result.set(phase.phaseNum, {
        completedPct: total > 0 ? (completed / total) * 100 : 0,
        inProgressPct: total > 0 ? (inProgress / total) * 100 : 0,
      });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases, progressKey]);

  const currentPhase = useMemo(
    () =>
      phases.find((p) => (phaseCompletion.get(p.phaseNum)?.completedPct ?? 0) < 100)?.phaseNum ??
      phases[phases.length - 1]?.phaseNum ??
      null,
    [phases, phaseCompletion],
  );

  return { phaseCompletion, currentPhase };
}

// ── Progress bars ──────────────────────────────────────────────────────────────

function BatchProgressBar({ projectId }: { projectId: string }) {
  const { data } = useProjectStatsQuery(projectId);
  const { completed, inProgress, total } = bucketStats(data);
  const completePct = total > 0 ? (completed / total) * 100 : 0;
  const inProgressPct = total > 0 ? (inProgress / total) * 100 : 0;
  const isComplete = completePct >= 100;
  const title = `${completed} complete · ${inProgress} in progress · ${
    total - completed - inProgress
  } unstarted`;

  return (
    <div className="flex items-center gap-2" title={title}>
      <div className="flex-1 h-1.5 rounded-full bg-bg-2 overflow-hidden flex">
        <div
          className="h-full transition-all duration-500 bg-signal"
          style={{ width: `${completePct}%` }}
        />
        <div
          className="h-full transition-all duration-500 bg-signal-tint"
          style={{ width: `${inProgressPct}%` }}
        />
      </div>
      <span
        className={`text-xs tabular-nums min-w-[3ch] text-right ${
          isComplete ? "text-signal-text-tint" : "text-fg-muted"
        }`}
      >
        {completePct.toFixed(0)}%
      </span>
    </div>
  );
}

// ── Batch card ─────────────────────────────────────────────────────────────────

function SegBatchCard({
  batch,
  onStart,
  isExpanded,
  onToggle,
  index,
  autoScroll,
}: {
  batch: BatchInfo;
  onStart: (projectId: string) => void;
  isExpanded: boolean;
  onToggle: () => void;
  index: number;
  autoScroll?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Scroll into view when the card was opened by the WorkList button (URL
  // param) — defer to next frame so the details panel has rendered into the
  // grid before we measure its position.
  useEffect(() => {
    if (!autoScroll || !cardRef.current) return;
    const id = requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [autoScroll]);
  // Horizontal placement of the 60vw details panel within the full-width
  // row it occupies — anchor to the side closest to this card's column so
  // it visually flows from the card that opened it.
  //
  // The grid is responsive (1 col → md:2 cols → lg:3 cols), so each
  // breakpoint needs its own mod and its own Tailwind class. Static
  // strings here are required for JIT to keep the classes.
  const mdMod = index % 2;
  const lgMod = index % 3;
  const mdAlign = mdMod === 0 ? "md:mr-auto" : "md:ml-auto";
  const lgAlign = lgMod === 0 ? "lg:mr-auto" : lgMod === 2 ? "lg:ml-auto" : "lg:mx-auto";
  const detailsAlign = `mx-auto ${mdAlign} ${lgAlign}`;

  return (
    <>
      <div ref={cardRef} className="bg-surface rounded border border-border overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{batch.batchLabel}</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-fg-muted">{batch.totalCases} cases</span>
            <button
              onClick={onToggle}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                isExpanded
                  ? "bg-surface-2 text-white"
                  : "text-fg-muted hover:text-white hover:bg-surface-2"
              }`}
            >
              {isExpanded ? "Hide Details" : "View Details"}
            </button>
          </div>
        </div>

        {/* Body: annotator + progress + start */}
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 w-32 shrink-0">
            <FaUser className="w-3 h-3 text-fg-muted" />
            <span className="text-xs text-fg-muted truncate">
              {batch.assignedTo?.name ?? "Unassigned"}
            </span>
          </div>
          <div className="flex-1">
            <BatchProgressBar projectId={batch.projectId} />
          </div>
          <button
            onClick={() => onStart(batch.projectId)}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium
                       bg-signal-tint text-signal-text-tint hover:bg-signal/30 transition-colors"
          >
            Start
            <FaChevronRight className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>

      {/* Inline details: spans the full grid row so subsequent batches are
          pushed down (no overlay). Capped at ~10 rows tall, scrollable. */}
      {isExpanded && (
        <div
          className={`col-span-full w-[60vw] max-w-[60vw] ${detailsAlign}
                      rounded border border-border
                      bg-bg-2 px-5 py-4 max-h-[550px] overflow-y-auto`}
        >
          <ProjectCaseList projectId={batch.projectId} />
        </div>
      )}
    </>
  );
}

// ── Grid class helper ──────────────────────────────────────────────────────────

function batchGridClass(count: number): string {
  // grid-flow-row-dense lets the col-span-full details panel push subsequent
  // cards down without leaving an empty cell on the row that opened it.
  if (count > 4) return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 grid-flow-row-dense";
  return "grid grid-cols-1 md:grid-cols-2 gap-4 grid-flow-row-dense";
}

// ── Main component ─────────────────────────────────────────────────────────────

export function SegStudyHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: projectsData, isLoading } = useProjectsQuery();
  const userId = useAtomValue(userIdAtom);
  const showStatsTab = canViewStats(userId);

  // Seed phase + expanded batch from `?project=<projectId>` (set by the
  // WorkList button on the labeling page so users land back on the same batch).
  const initialProjectId = useRef(searchParams.get("project")).current;
  const initialFromParam = useMemo(() => {
    if (!initialProjectId) return null;
    const m = initialProjectId.match(US3D_REGEX);
    if (!m) return null;
    return { phaseNum: parseInt(m[1], 10), projectId: initialProjectId };
  }, [initialProjectId]);

  const [selectedPhase, setSelectedPhase] = useState<number | null>(
    initialFromParam?.phaseNum ?? null,
  );
  const [showInstructions, setShowInstructions] = useState(false);
  // Single-source-of-truth for which batch's details panel is open. Only one
  // can be expanded at a time across all batches in the active phase.
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(
    initialFromParam?.projectId ?? null,
  );

  const phases = useMemo(() => {
    if (!projectsData?.projects) return [];

    const phaseMap = new Map<number, PhaseInfo>();

    for (const project of projectsData.projects) {
      const match = project.project_id.match(US3D_REGEX);
      if (!match) continue;

      const phaseNum = parseInt(match[1], 10);
      const batchNum = parseInt(match[2], 10);

      if (!phaseMap.has(phaseNum)) {
        phaseMap.set(phaseNum, {
          phaseNum,
          phaseLabel: `Phase ${phaseNum}`,
          description: "",
          batches: [],
        });
      }

      phaseMap.get(phaseNum)!.batches.push({
        batchNum,
        batchLabel: `Batch ${batchNum}`,
        projectId: project.project_id,
        totalCases: project.total_cases,
        studyUids: project.study_uids ?? [],
        assignedTo: project.assigned_to,
      });
    }

    return Array.from(phaseMap.values())
      .sort((a, b) => a.phaseNum - b.phaseNum)
      .map((phase) => ({
        ...phase,
        description: PHASE_DESCRIPTIONS[phase.phaseNum] ?? "",
        batches: phase.batches.sort((a, b) => a.batchNum - b.batchNum),
      }));
  }, [projectsData]);

  const activePhaseNum = selectedPhase ?? phases[0]?.phaseNum ?? null;
  const activePhase = phases.find((p) => p.phaseNum === activePhaseNum) ?? null;

  const { currentPhase, phaseCompletion } = usePhaseProgress(phases);

  // Count unique cases across all phases using study UIDs
  const uniqueCaseCount = useMemo(() => {
    const allUids = new Set<string>();
    for (const phase of phases) {
      for (const batch of phase.batches) {
        for (const uid of batch.studyUids) {
          allUids.add(uid);
        }
      }
    }
    return allUids.size;
  }, [phases]);
  const totalBatches = phases.reduce((sum, p) => sum + p.batches.length, 0);

  const handleStart = (projectId: string) => {
    navigate(`/labeling?project=${encodeURIComponent(projectId)}`);
  };

  return (
    <>
      <Helmet>
        <title>Urinary Stone 3D Annotations | a2z</title>
      </Helmet>

      <div className="h-full overflow-auto text-white page-premium">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-white">Urinary Stone 3D Annotations</h1>
              {currentPhase != null && (
                <span className="text-xs font-normal text-fg-muted align-middle">
                  Current Phase: {currentPhase}
                </span>
              )}
            </div>
            <p className="text-fg-muted text-sm">3D segmentation annotation for urinary stones</p>
            <div className="flex items-center gap-4 mt-3 text-xs text-fg-muted">
              <span>{uniqueCaseCount} unique cases</span>
              <span className="text-fg-muted">|</span>
              <span>{phases.length} phases</span>
              <span className="text-fg-muted">|</span>
              <span>{totalBatches} batches</span>
            </div>
          </div>

          {/* Study Details + Phase Overview — 2-column: left content, right video */}
          {phases.length > 0 && (
            <div className="mb-8 grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-5">
              {/* Left column: study details + phase overview in one panel */}
              <div className="bg-surface rounded border border-border p-5 flex flex-col">
                {/* Study Details */}
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xs font-bold text-white uppercase tracking-wider">
                    Study Details
                  </h2>
                  <button
                    onClick={() => setShowInstructions(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium
                               bg-signal-tint text-signal-text-tint hover:bg-signal/30 transition-colors"
                  >
                    <FaFileAlt className="w-3 h-3" />
                    Instructions
                  </button>
                </div>
                <p className="text-sm text-fg-muted leading-relaxed mb-5">
                  Annotate all visible urinary tract stones using 3D segmentation tools. Each stone
                  is a separate annotation with metadata (laterality, location, obstruction, shape).
                  Draw on any view, verify in{" "}
                  <span className="text-white font-medium">all three planes</span> before saving.
                </p>

                {/* Phase Overview */}
                <h2 className="text-xs font-bold text-white uppercase tracking-wider mb-4">
                  Phase Overview
                </h2>
                <div className="flex gap-3 overflow-x-auto" style={{ minWidth: "min-content" }}>
                  {phases.map((phase) => {
                    const { completedPct = 0, inProgressPct = 0 } =
                      phaseCompletion.get(phase.phaseNum) ?? {};
                    const isComplete = completedPct >= 100;
                    const isCurrent = phase.phaseNum === currentPhase;
                    const isActive = phase.phaseNum === activePhaseNum;
                    const uniquePhaseCases = new Set(phase.batches.flatMap((b) => b.studyUids))
                      .size;
                    return (
                      <button
                        key={phase.phaseNum}
                        onClick={() => setSelectedPhase(phase.phaseNum)}
                        className={`shrink-0 w-56 rounded p-4 text-left transition-colors border ${
                          isActive
                            ? "border-signal-stroke bg-signal-ambient"
                            : "border-border bg-surface hover:border-border-strong"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-sm font-semibold text-white">
                            {phase.phaseLabel}
                          </span>
                          {isCurrent && (
                            <span className="px-1.5 py-0.5 rounded-sharp text-[10px] font-medium bg-signal-tint text-signal-text-tint border border-signal-stroke">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-fg-muted mb-3 line-clamp-2 min-h-[2.75em]">
                          {phase.description || "\u00A0"}
                        </p>
                        <div className="flex items-center gap-3 text-[11px] text-fg-muted mb-3">
                          <span>{phase.batches.length} batches</span>
                          <span>{uniquePhaseCases} cases</span>
                        </div>
                        <div
                          className="flex items-center gap-2"
                          title={`${completedPct.toFixed(0)}% complete \u00B7 ${inProgressPct.toFixed(0)}% in progress`}
                        >
                          <div className="flex-1 h-1.5 rounded-full bg-bg-2 overflow-hidden flex">
                            <div
                              className="h-full transition-all duration-500 bg-signal"
                              style={{ width: `${completedPct}%` }}
                            />
                            <div
                              className="h-full transition-all duration-500 bg-signal-tint"
                              style={{ width: `${inProgressPct}%` }}
                            />
                          </div>
                          <span
                            className={`text-xs tabular-nums min-w-[3ch] text-right ${
                              isComplete ? "text-signal-text-tint" : "text-fg-muted"
                            }`}
                          >
                            {completedPct.toFixed(0)}%
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right column: demo video + study stats in a tabbed panel */}
              <RightMediaPanel
                activePhase={activePhase}
                allPhases={phases}
                canViewStats={showStatsTab}
              />
            </div>
          )}

          {/* Batch grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-border-strong border-t-signal rounded-full animate-spin" />
            </div>
          ) : phases.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-fg-muted mb-2">No urinary stone batches found</div>
              <div className="text-fg-muted text-xs">
                Upload worklists with prefix &quot;us3d-&quot; to get started
              </div>
            </div>
          ) : (
            <div className={batchGridClass(activePhase?.batches.length ?? 0)}>
              {activePhase?.batches.map((batch, i) => {
                const isExpanded = expandedBatchId === batch.projectId;
                return (
                  <SegBatchCard
                    key={batch.projectId}
                    batch={batch}
                    onStart={handleStart}
                    isExpanded={isExpanded}
                    onToggle={() =>
                      setExpandedBatchId((prev) =>
                        prev === batch.projectId ? null : batch.projectId,
                      )
                    }
                    index={i}
                    autoScroll={initialFromParam?.projectId === batch.projectId}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <SegStudyInstructionsDialog
        projectId={(activePhase ?? phases[0])?.batches[0]?.projectId ?? null}
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
    </>
  );
}

/**
 * Fetches the worklist for a representative batch to discover its
 * `instructions_key`, then renders the shared InstructionsDialog. The first
 * batch's project is the authenticated source for the shared S3 instructions.
 */
function SegStudyInstructionsDialog({
  projectId,
  open,
  onClose,
}: {
  projectId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  // Skip the network call until the user actually opens the dialog.
  const worklistQuery = useWorklistQuery(open && projectId ? projectId : "");
  const instructionsKey = worklistQuery.data?.instructions_key ?? null;
  return (
    <InstructionsDialog
      projectId={projectId}
      instructionsKey={instructionsKey}
      open={open}
      onClose={onClose}
    />
  );
}

// ── Right column media/stats panel ─────────────────────────────────────────────

type MediaTab = "video" | "stats";

function RightMediaPanel({
  activePhase,
  allPhases,
  canViewStats,
}: {
  activePhase: PhaseInfo | null;
  allPhases: PhaseInfo[];
  canViewStats: boolean;
}) {
  const [tab, setTab] = useState<MediaTab>("video");
  // Derive the active tab from `canViewStats` instead of patching state via
  // useEffect: if the gate flips false (impersonation ends, token refresh
  // narrows claims), force the rendered tab to Video without a second render
  // pass. Storing the user's preference in `tab` lets it return when the gate
  // re-opens. (Anti-pattern dodged: Effect adjusting state on prop change.)
  const effectiveTab: MediaTab = canViewStats ? tab : "video";
  const showStats = effectiveTab === "stats";

  return (
    <div className="rounded-lg border border-border bg-surface panel-premium overflow-hidden flex flex-col h-[360px]">
      {/* Tab header — Stats tab only visible to canViewStats users */}
      <div className="flex items-center gap-1 px-1.5 pt-1.5">
        <TabButton active={effectiveTab === "video"} onClick={() => setTab("video")}>
          Video
        </TabButton>
        {canViewStats && (
          <TabButton active={effectiveTab === "stats"} onClick={() => setTab("stats")}>
            Stats
          </TabButton>
        )}
      </div>

      {/* Tab content */}
      <div className="flex-1 flex items-center justify-center p-1.5 min-h-0">
        {showStats ? (
          <StudyStatsPanel activePhase={activePhase} allPhases={allPhases} />
        ) : (
          <video
            className="w-full h-full rounded object-contain"
            src="/videos/nninteractive-demo.mp4"
            controls
            loop
            muted
            playsInline
          />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        active ? "bg-primary/20 text-primary" : "text-fg-muted hover:text-fg"
      }`}
    >
      {children}
    </button>
  );
}

// ── Study stats aggregation ────────────────────────────────────────────────────

type StatsScope = "phase" | "all";

interface AggregatedStats {
  totalStones: number;
  completedCases: number;
  totalCases: number;
  /** Per-day counts for last 10 days. Index 0 = 9 days ago, 9 = today. */
  dailyStones: number[];
  dailyCases: number[];
  isLoading: boolean;
}

const SPARK_DAYS = 10;

function useAggregatedStats(batches: BatchInfo[]): AggregatedStats {
  const statsQueries = useQueries({
    queries: batches.map((b) => ({
      queryKey: ["annotation-stats", b.projectId] as const,
      queryFn: async () => {
        const response = await axios.get<AnnotationStatsResponse>(
          `${getApiBaseUrl()}/api/labeling/projects/${b.projectId}/annotation-stats`,
        );
        return response.data;
      },
      refetchInterval: 30000,
      staleTime: 30000,
      retry: 2,
    })),
  });

  // `dataUpdatedAt` increments on every fresh response from TanStack Query,
  // covering both content rotations at stable length and partial loads (one
  // batch fresh, others stale). It's the right signal for the memo dep —
  // `statsQueries` itself is referentially unstable across renders so we
  // can't depend on it directly.
  const dataSignature = statsQueries.map((q) => q.dataUpdatedAt).join(",");

  // Phase→all scope switch: phase-1 batches stay warm-cached, other phases
  // refetch cold. Use every(isSuccess) + some(isFetching) so the UI shows a
  // spinner instead of phase-1 totals labeled "All phases" for ~30s.
  const allSuccess = statsQueries.every((q) => q.isSuccess);
  const anyFetching = statsQueries.some((q) => q.isFetching);
  const isLoading = !allSuccess || anyFetching;

  return useMemo(
    () => {
      let totalStones = 0;
      let completedCases = 0;
      let totalCases = 0;
      const dailyStones = Array.from<number>({ length: SPARK_DAYS }).fill(0);
      const studiesPerDay: Array<Set<string>> = Array.from(
        { length: SPARK_DAYS },
        () => new Set<string>(),
      );
      for (const q of statsQueries) {
        if (!q.data) continue;
        totalStones += q.data.total_annotations;
        completedCases += q.data.completed_cases;
        totalCases += q.data.total_cases;
        for (const a of q.data.recent_activity) {
          const idx = dayIndex(a.annotated_at, SPARK_DAYS);
          if (idx < 0) continue;
          dailyStones[idx] += 1;
          studiesPerDay[idx].add(a.study_instance_uid);
        }
      }
      const dailyCases = studiesPerDay.map((s) => s.size);
      return {
        totalStones,
        completedCases,
        totalCases,
        dailyStones,
        dailyCases,
        isLoading,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statsQueries is referentially unstable each render; dataSignature is the stable content proxy that ticks on every fresh response.
    [dataSignature, isLoading],
  );
}

function StudyStatsPanel({
  activePhase,
  allPhases,
}: {
  activePhase: PhaseInfo | null;
  allPhases: PhaseInfo[];
}) {
  const [scope, setScope] = useState<StatsScope>("phase");

  const phaseBatches = activePhase?.batches ?? [];
  const allBatches = useMemo(() => allPhases.flatMap((p) => p.batches), [allPhases]);
  const batches = scope === "phase" ? phaseBatches : allBatches;

  const { totalStones, completedCases, totalCases, dailyStones, dailyCases, isLoading } =
    useAggregatedStats(batches);

  const scopeLabel =
    scope === "phase"
      ? activePhase
        ? `Phase ${activePhase.phaseNum}`
        : "Selected phase"
      : "All phases";

  const stonesPerCase = completedCases > 0 ? totalStones / completedCases : 0;

  return (
    <div className="w-full h-full flex flex-col gap-4 p-1">
      {/* Scope toggle */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-bg-2 border border-border self-start">
        <button
          onClick={() => setScope("phase")}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            scope === "phase" ? "bg-primary/20 text-primary" : "text-fg-muted hover:text-fg"
          }`}
        >
          Selected phase
        </button>
        <button
          onClick={() => setScope("all")}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            scope === "all" ? "bg-primary/20 text-primary" : "text-fg-muted hover:text-fg"
          }`}
        >
          All phases
        </button>
      </div>

      {/* Stat cards — side by side, sparklines fill remaining height */}
      <div className="flex-1 grid grid-cols-2 gap-3 min-h-0">
        <StatCard
          icon={<FaCircle className="w-3.5 h-3.5" />}
          label="Stones"
          value={isLoading ? null : totalStones}
          sublabel="annotated"
          accentClass="text-fg-muted"
          ringClass="ring-border-strong"
          sparkData={dailyStones}
          sparkBarClass="bg-fg-muted"
        />
        <StatCard
          icon={<FaCheckCircle className="w-3.5 h-3.5" />}
          label="Cases"
          value={isLoading ? null : completedCases}
          sublabel={totalCases > 0 ? `of ${totalCases.toLocaleString()}` : "annotated"}
          accentClass="text-signal-text-tint"
          ringClass="ring-signal-ring"
          sparkData={dailyCases}
          sparkBarClass="bg-signal"
        />
      </div>

      {/* Footer: scope + ratio */}
      <div className="flex flex-col">
        <div className="rounded-lg border border-border bg-surface px-4 py-3 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-fg-muted">Scope</span>
            <span className="text-sm font-semibold text-white">{scopeLabel}</span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-[10px] uppercase tracking-wide text-fg-muted">Stones / case</span>
            <span className="text-sm font-semibold text-white tabular-nums">
              {isLoading ? "—" : stonesPerCase.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Sparkline({ data, barClass }: { data: number[]; barClass: string }) {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-[3px] h-10">
      {/* key={i} is intentional: each bar is a positional bucket (day N ago), and the array length is fixed (SPARK_DAYS = 10), so the index IS the stable identity. */}
      {data.map((v, i) => {
        const heightPct = (v / max) * 100;
        const isToday = i === data.length - 1;
        const dayLabel = i === data.length - 1 ? "Today" : `${data.length - 1 - i}d ago`;
        return (
          <div
            key={i}
            className="flex-1 flex flex-col justify-end h-full"
            title={`${dayLabel}: ${v}`}
          >
            <div
              className={`w-full rounded-xs transition-all duration-500 ${barClass} ${
                v === 0 ? "opacity-20" : isToday ? "opacity-100" : "opacity-70"
              }`}
              style={{ height: `${Math.max(heightPct, v > 0 ? 8 : 4)}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  sublabel,
  accentClass,
  ringClass,
  sparkData,
  sparkBarClass,
}: {
  icon: ReactNode;
  label: string;
  value: number | null;
  sublabel: string;
  accentClass: string;
  ringClass: string;
  sparkData?: number[];
  sparkBarClass?: string;
}) {
  return (
    <div
      className={`relative rounded border border-border bg-surface
                  ring-1 ${ringClass} px-4 py-3 flex flex-col justify-between min-h-0`}
    >
      <div className="flex items-center gap-2">
        <span className={accentClass}>{icon}</span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-bold text-white tabular-nums">
          {value == null ? "—" : value.toLocaleString()}
        </span>
        <span className="text-[11px] text-fg-muted">{sublabel}</span>
      </div>
      {sparkData && sparkBarClass && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wide text-fg-muted mb-1">
            Recent · 10 days
          </div>
          <Sparkline data={sparkData} barClass={sparkBarClass} />
        </div>
      )}
    </div>
  );
}
